import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as lodash from 'lodash';
import React, { useCallback, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement, typeguards } from '@finley-technologies/finley-types';

import { useDraggableRow } from '../../../../../../common-frontend/components/DragAndDropTable/hooks';
import { AddIconButton } from '../../../../../../common-frontend/components/IconButton';
import { CreditAgreementFullArtifactWipConfigType } from '../../../../types';
import { getIterationKey } from '../../../common';
import { ReportingOutputDocxColumnConfig } from './ReportingOutputDocxColumnConfig';

const getColumnConfigShell = (
  index: number,
): CreditAgreement.Config.Reporting.Outputs.Docx.ColumnConfig => {
  return {
    name: `columnName${index}`,
    propertyName: 'propertyName',
    format: 'none',
    order: index,
    type: 'text',
  };
};

const addConfig = async (params: {
  basePropertyKey: string;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
}): Promise<void> => {
  const { basePropertyKey, wipConfig } = params;
  const columnConfigs =
    getIn(wipConfig.formProps.values, basePropertyKey) ?? [];

  finleyAssert(
    typeguards.creditAgreement.isDocxColumnConfigs(columnConfigs),
    `Retrieved non-DocxColumnConfigs structure for [key:${basePropertyKey}]`,
  );

  const configShell = getColumnConfigShell(columnConfigs.length);
  const configsCopy = lodash.cloneDeep(columnConfigs);
  configsCopy.push(configShell);
  await wipConfig.formProps.setFieldValue(basePropertyKey, configsCopy);
};

type ReportingOutputDocxColumnConfigContainerProps = {
  basePropertyKey: string;
  columnNames?: string[];
  columnConfigs: CreditAgreement.Config.Reporting.Outputs.Docx.ColumnConfig[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
};
export const ReportingOutputDocxColumnConfigContainer = ({
  basePropertyKey,
  columnConfigs,
  columnNames,
  wipConfig,
}: ReportingOutputDocxColumnConfigContainerProps) => {
  const {
    useRowState,
    moveRow,
    isOrderSame,
  } = useDraggableRow<CreditAgreement.Config.Reporting.Outputs.Docx.ColumnConfig>(
    { comparisonKey: 'order' },
  );
  const [rows, setRows, initialRows] = useRowState(columnConfigs);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const updateConfigs = useCallback(async (): Promise<void> => {
    if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
      return;
    }

    const updatedConfigs = rows.map((config, index) => ({
      ...lodash.cloneDeep(config),
      order: index,
    }));

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: basePropertyKey,
      updatedProperty: updatedConfigs,
      setIsSaveLoading: setIsUpdating,
    });
  }, [wipConfig, rows, basePropertyKey]);

  return (
    <Box id="column-configs-container" mb="5">
      <Flex alignItems="center" mb="2">
        <Text fontWeight="bold" mr="2">
          Column Configs
        </Text>

        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add column config"
            label="Add Column Config"
            onClick={async () => addConfig({ basePropertyKey, wipConfig })}
          />
        )}
        {wipConfig?.isMutateAllowed(basePropertyKey) && !isOrderSame && (
          <>
            <Button
              colorScheme="green"
              variant="outline"
              onClick={updateConfigs}
              isLoading={isUpdating}
              marginRight="10px"
            >
              Save order
            </Button>
            <Button
              colorScheme="red"
              variant="outline"
              onClick={() => {
                setRows(initialRows);
              }}
            >
              Reset order
            </Button>
          </>
        )}
      </Flex>
      {rows.length > 0 ? (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              {wipConfig?.isMutateAllowed(basePropertyKey) && <Th />}
              <Th width="10%">Order</Th>
              <Th width="20%">Column Name</Th>
              <Th width="30%">Column Property Name</Th>
              <Th width="20%">Type</Th>
              <Th width="20%">Format</Th>
              {wipConfig?.isMutateAllowed(basePropertyKey) && (
                <>
                  <Th textAlign="center" width="5%">
                    Edit
                  </Th>
                  <Th textAlign="center" width="5%">
                    Delete
                  </Th>
                </>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((config, idx) => {
              const id = getIterationKey(config);
              return (
                <ReportingOutputDocxColumnConfig
                  basePropertyKey={basePropertyKey}
                  columnConfig={config}
                  columnPropertyNames={columnNames}
                  idx={idx}
                  id={id}
                  key={id}
                  moveRow={moveRow}
                  wipConfig={wipConfig}
                />
              );
            })}
          </Tbody>
        </Table>
      ) : (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no columns configured
        </Text>
      )}
    </Box>
  );
};
